import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { countryConfig } from '../../country';
import { Seo } from '../../components/seo';
import { StatTile } from '../../components/charts/stat-tile';
import { RankedBars } from '../../components/charts/ranked-bars';
import { useLocale, useT } from '../../i18n/locale-context';
import { usePageTitle } from '../../lib/page-title';
import { track } from '../../lib/funnel';
import { fetchMarketStats, type MarketRange, type MarketStats } from '../../lib/market-client';
import { formatBudget, formatBudgetCompact, formatDateTime, withCurrency } from '../../lib/format';
import { localePath } from '../../i18n/locale-path';
import { countryNameEn } from '../../lib/country-name-en';
import { ChannelSubscribeCta } from '../../components/channel-subscribe-cta';

const SITE_ORIGIN = countryConfig.brand.siteUrl;

/** JSON-LD `Dataset` country + keywords. UZ keeps what the page always
 *  shipped; kaztender.com/market named Uzbekistan in both until 2026-10-15. */
const DATASET_COUNTRY = countryNameEn(countryConfig.code);
const DATASET_KEYWORDS: string[] =
  countryConfig.code === 'kz'
    ? ['госзакупки', 'мемлекеттік сатып алу', 'tender', 'Kazakhstan']
    : countryConfig.code === 'es'
      ? ['licitaciones públicas', 'contratación pública', 'tender', 'Spain']
      : countryConfig.code === 'cl'
        ? ['licitaciones públicas', 'compras públicas', 'Mercado Público', 'tender', 'Chile']
        : ['davlat xaridlari', 'госзакупки', 'tender', 'Uzbekistan'];
const KICKER = 'text-[11px] font-bold uppercase tracking-[0.06em] text-ink-muted';

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-edge bg-bone-surface p-5">
      <h2 className={`mb-3 ${KICKER}`}>{title}</h2>
      {children}
    </section>
  );
}

/** Public, indexable market overview: aggregate opportunities + outcomes, served
 *  from a Redis-cached aggregation. No individual buyer/supplier names. */
export function MarketDashboard() {
  const t = useT();
  const { locale } = useLocale();
  usePageTitle(t('market.title'));
  // `market_viewed` fired with no meta, so it was 100% null and un-sliceable
  // (events-health audit, 2026-08-19). Stamp the arrival `source` (?src param,
  // default 'direct') and the `locale` so uz-vs-ru usage of this public page is
  // answerable. Keyed on locale so a language switch (which remounts) re-fires.
  useEffect(() => {
    const source = new URLSearchParams(window.location.search).get('src') ?? 'direct';
    track('market_viewed', { source, locale });
  }, [locale]);

  // Optional date-range filter (progressive enhancement). Empty inputs = no
  // range = the default all-time dashboard, whose query key stays ['market',
  // locale] so the SSR/prerender seed hydrates unchanged. A range is applied
  // only once BOTH bounds are set and ordered, and it adds from/to to the key
  // (a distinct, uncached fetch) — never the default one.
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const range = useMemo<MarketRange | null>(
    () => (from && to && from <= to ? { from, to } : null),
    [from, to],
  );

  const { data, isLoading } = useQuery({
    queryKey: range ? ['market', locale, range.from, range.to] : ['market', locale],
    queryFn: () => fetchMarketStats(locale, range ?? undefined),
  });

  const grouped = (n: number): string => formatBudget(String(Math.round(n))) ?? '0';
  const compact = (n: number): string => formatBudgetCompact(String(Math.round(n)), locale) ?? '0';
  const som = t('market.som');

  const regionItems = (m: MarketStats) =>
    m.byRegion.map((r) => ({
      label: r.name ?? r.region,
      value: r.budgetUzs,
      valueText: withCurrency(compact(r.budgetUzs), som),
      sub: t('market.count', { count: r.tenders }),
    }));
  const categoryItems = (m: MarketStats) =>
    m.byCategory.map((c) => ({
      label: c.name ?? c.slug,
      value: c.budgetUzs,
      valueText: withCurrency(compact(c.budgetUzs), som),
      sub: t('market.count', { count: c.tenders }),
    }));

  return (
    <>
      <Seo
        title={t('market.seo.title')}
        description={t('market.seo.description')}
        canonical="/market"
        lang={locale}
        // Dataset markup carrying the machine-readable freshness stamp that until
        // now was only visible as the "Данные на …" prose line. dateModified lets
        // AI/GEO extractors trust the recency of the aggregate figures.
        jsonLd={
          data
            ? {
                '@context': 'https://schema.org',
                '@graph': [
                  {
                    '@type': 'Dataset',
                    name: t('market.seo.title'),
                    description: t('market.seo.description'),
                    url: `${SITE_ORIGIN}${localePath('/market', locale)}`,
                    isAccessibleForFree: true,
                    inLanguage: locale,
                    creator: {
                      '@type': 'Organization',
                      name: countryConfig.brand.name,
                      url: `${SITE_ORIGIN}/`,
                    },
                    // The aggregate covers Uzbekistan-wide public procurement. These
                    // fields make the Dataset self-describing for Google Dataset
                    // Search + AI answer engines, which previously had only name +
                    // description to go on.
                    spatialCoverage: { '@type': 'Country', name: DATASET_COUNTRY },
                    // Name the actual measured variables (was a duplicate of the
                    // title) so Dataset Search / AI engines can topic-match.
                    variableMeasured: [
                      t('market.kpi.active'),
                      t('market.kpi.budget'),
                      t('market.kpi.newWeek'),
                      t('market.kpi.awardedCount'),
                      t('market.byRegion'),
                      t('market.byCategory'),
                    ],
                    keywords: DATASET_KEYWORDS,
                    dateModified: new Date(data.generatedAt).toISOString(),
                  },
                  {
                    // Home → Market breadcrumb (Google renders it in the SERP).
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                      {
                        '@type': 'ListItem',
                        position: 1,
                        name: t('nav.catalog'),
                        item: `${SITE_ORIGIN}${localePath('/', locale)}`,
                      },
                      {
                        '@type': 'ListItem',
                        position: 2,
                        name: t('market.title'),
                        item: `${SITE_ORIGIN}${localePath('/market', locale)}`,
                      },
                    ],
                  },
                ],
              }
            : undefined
        }
      />
      <div className="mx-auto max-w-3xl lg:max-w-5xl">
        <ChannelSubscribeCta surface="market" />
        <h1 className="text-[22px] font-bold text-ink">{t('market.title')}</h1>
        <p className="mb-4 mt-1 text-[14px] text-ink-muted">{t('market.subtitle')}</p>

        {/* Date-range filter. Rendered outside the loading branch so it is always
            usable; empty by default so the prerendered dashboard is all-time. */}
        <div className="mb-5 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium text-ink-muted">{t('market.range.from')}</span>
            <input
              type="date"
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
              className="h-9 min-w-0 rounded-md border border-edge bg-bone-surface px-2.5 text-[13px] text-ink outline-none transition-colors focus:border-brand focus:shadow-[0_0_0_3px_rgba(10,92,92,0.1)] [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium text-ink-muted">{t('market.range.to')}</span>
            <input
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
              className="h-9 min-w-0 rounded-md border border-edge bg-bone-surface px-2.5 text-[13px] text-ink outline-none transition-colors focus:border-brand focus:shadow-[0_0_0_3px_rgba(10,92,92,0.1)] [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60"
            />
          </label>
          {range && (
            <button
              type="button"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
              className="h-9 rounded-md border border-edge px-3 text-[13px] text-ink-muted transition-colors hover:border-brand hover:text-ink"
            >
              {t('market.range.reset')}
            </button>
          )}
        </div>

        {isLoading || !data ? (
          <div className="h-64 animate-pulse rounded-lg border border-edge bg-bone-surface" />
        ) : (
          <div className="flex flex-col gap-6">
            {/* Self-contained, numeric summary sentence built from the live
                figures. The KPI grid below is label:value tiles an AI engine
                can't lift as a quote; this one prose line is the citable unit
                (GEO audit, 2026-08-25) — and orients a first-time human visitor
                before the tiles. */}
            <p className="text-[14px] leading-relaxed text-ink-soft">
              {t('market.summary', {
                active: grouped(data.activeTenders),
                budget: withCurrency(compact(data.activeBudgetUzs), som),
                newWeek: grouped(data.newThisWeek),
              })}
            </p>

            {/* A — KPIs */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
              <StatTile label={t('market.kpi.active')} value={grouped(data.activeTenders)} />
              <StatTile
                label={t('market.kpi.budget')}
                value={withCurrency(compact(data.activeBudgetUzs), som)}
              />
              <StatTile label={t('market.kpi.newWeek')} value={grouped(data.newThisWeek)} />
              <StatTile label={t('market.kpi.closingSoon')} value={grouped(data.closingSoon)} />
              <StatTile
                label={t('market.kpi.awardedCount')}
                value={grouped(data.awarded12moCount)}
              />
              <StatTile
                label={t('market.kpi.awardedVolume')}
                value={withCurrency(compact(data.awarded12moVolumeUzs), som)}
              />
            </div>
            {/* Direct contracts (signed with no tender) are kept OUT of the two
                award tiles and shown as this one labelled line (owner,
                2026-10-17) — mixed in they were 38 of 89 trillion so'm. */}
            {data.direct12moCount ? (
              <p className="-mt-3 text-[12.5px] leading-snug text-ink-muted">
                {t('market.direct', {
                  count: grouped(data.direct12moCount),
                  volume: withCurrency(compact(data.direct12moVolumeUzs ?? 0), som),
                })}
              </p>
            ) : null}

            {/* B — Opportunities */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Panel title={t('market.byRegion')}>
                <RankedBars items={regionItems(data)} />
              </Panel>
              <Panel title={t('market.byCategory')}>
                <RankedBars items={categoryItems(data)} />
              </Panel>
            </div>

            {/* C — Outcomes */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Panel title={t('market.competition')}>
                <RankedBars
                  items={data.competitionBySector.map((s) => ({
                    label: s.name ?? s.section,
                    value: s.medianParticipants,
                    valueText: String(s.medianParticipants),
                    sub: t('market.count', { count: s.n }),
                  }))}
                />
              </Panel>
              <Panel title={t('market.discount')}>
                <RankedBars
                  items={data.discountBySector.map((s) => ({
                    label: s.name ?? s.section,
                    value: s.medianDiscountPct,
                    valueText: `${s.medianDiscountPct}%`,
                    sub: t('market.count', { count: s.n }),
                  }))}
                />
              </Panel>
            </div>

            <p className="text-[12px] text-ink-muted">
              {t('market.generatedAt', { date: formatDateTime(data.generatedAt, locale) ?? '' })}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
